const asArray = value => (Array.isArray(value) ? value : []);

export const DEVICE_CONFIG_SOURCE = {
    MAIN: 'main',
    MODEL: 'model',
    OPERATOR: 'operator',
    CONTRACTOR: 'contractor',
    COLLECTION_POINT: 'collectionPoint',
    DEVICE: 'device',
    DEVICE_GROUP: 'deviceGroup',
};

DEVICE_CONFIG_SOURCE.MAAS_LOOP = DEVICE_CONFIG_SOURCE.MAIN;

export const DEVICE_CONFIG_DRAWER_ID = 'select';

export const getDeviceConfigDrawerDeviceId = elementId => {
    if (elementId == null || elementId === '' || String(elementId) === DEVICE_CONFIG_DRAWER_ID) {
        return null;
    }

    return elementId;
};

const SOURCE_BY_KEY = {
    maas_loop: DEVICE_CONFIG_SOURCE.MAIN,
    maasloop: DEVICE_CONFIG_SOURCE.MAIN,
    maas: DEVICE_CONFIG_SOURCE.MAIN,
    main: DEVICE_CONFIG_SOURCE.MAIN,
    global: DEVICE_CONFIG_SOURCE.MAIN,
    default: DEVICE_CONFIG_SOURCE.MAIN,
    parameter: DEVICE_CONFIG_SOURCE.MAIN,
    glowny_parametr: DEVICE_CONFIG_SOURCE.MAIN,
    model: DEVICE_CONFIG_SOURCE.MODEL,
    operator: DEVICE_CONFIG_SOURCE.OPERATOR,
    contractor: DEVICE_CONFIG_SOURCE.CONTRACTOR,
    collection_point: DEVICE_CONFIG_SOURCE.COLLECTION_POINT,
    collectionpoint: DEVICE_CONFIG_SOURCE.COLLECTION_POINT,
    device: DEVICE_CONFIG_SOURCE.DEVICE,
    device_group: DEVICE_CONFIG_SOURCE.DEVICE_GROUP,
    devicegroup: DEVICE_CONFIG_SOURCE.DEVICE_GROUP,
    group: DEVICE_CONFIG_SOURCE.DEVICE_GROUP,
    device_group_config: DEVICE_CONFIG_SOURCE.DEVICE_GROUP,
    group_config: DEVICE_CONFIG_SOURCE.DEVICE_GROUP,
};

const SOURCE_PRIORITY = {
    [DEVICE_CONFIG_SOURCE.MAIN]: 0,
    [DEVICE_CONFIG_SOURCE.MODEL]: 1,
    [DEVICE_CONFIG_SOURCE.OPERATOR]: 2,
    [DEVICE_CONFIG_SOURCE.CONTRACTOR]: 3,
    [DEVICE_CONFIG_SOURCE.COLLECTION_POINT]: 4,
    [DEVICE_CONFIG_SOURCE.DEVICE]: 5,
    [DEVICE_CONFIG_SOURCE.DEVICE_GROUP]: 6,
};

const normalizeSourceKey = source =>
    String(source)
        .trim()
        .replace(/([a-z])([A-Z])/g, '$1_$2')
        .replace(/[\s-]+/g, '_')
        .toLowerCase();

export const canViewDeviceConfig = permissions => Boolean(permissions?.isAdmin || permissions?.isBok);

export const canRefreshDeviceConfig = permissions => Boolean(permissions?.isAdmin);

export const parseDeviceConfigGenerationDate = payload => {
    const root = payload?.data && typeof payload.data === 'object' && !Array.isArray(payload.data) ? payload.data : payload;
    const dateValue = root?.finished_at ?? root?.finishedAt;
    if (!dateValue) {
        return null;
    }

    const normalized = typeof dateValue === 'string' ? dateValue.replace(' ', 'T') : dateValue;
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const stringifyDeviceConfigValue = value => {
    if (value == null) {
        return '';
    }

    if (typeof value === 'object') {
        return JSON.stringify(value);
    }

    return String(value);
};

export const normalizeDeviceConfigSource = source => {
    if (source == null || source === '') {
        return '';
    }

    if (typeof source === 'object') {
        return normalizeDeviceConfigSource(source.type ?? source.source ?? source.origin ?? source.name);
    }

    return SOURCE_BY_KEY[normalizeSourceKey(source)] ?? source;
};

const firstDefined = (...values) => values.find(value => value != null && value !== '');

const getPlainText = value => {
    if (value == null || value === '') {
        return '';
    }

    if (typeof value !== 'object') {
        return String(value);
    }

    return String(firstDefined(value.code, value.name, value.label, value.title, value.serialNumber, value.customId, value.id) ?? '');
};

export const formatCollectionPointSourceValue = collectionPoint => {
    if (collectionPoint == null || collectionPoint === '') {
        return '';
    }

    if (typeof collectionPoint !== 'object') {
        return String(collectionPoint);
    }

    const address = collectionPoint.address && typeof collectionPoint.address === 'object' ? collectionPoint.address : collectionPoint;
    const city = firstDefined(collectionPoint.city, address.city);
    const street = firstDefined(typeof collectionPoint.address === 'string' ? collectionPoint.address : null, address.address, collectionPoint.street);

    if (city && street) {
        return `${city}, ${street}`;
    }

    return String(firstDefined(city, street, collectionPoint.customId, collectionPoint.name, collectionPoint.id) ?? '');
};

const formatSourceValueByType = (source, value) => {
    if (value == null || value === '') {
        return '';
    }

    if (source === DEVICE_CONFIG_SOURCE.COLLECTION_POINT) {
        return formatCollectionPointSourceValue(value);
    }

    if (source === DEVICE_CONFIG_SOURCE.MODEL) {
        return getPlainText(value);
    }

    if (source === DEVICE_CONFIG_SOURCE.DEVICE) {
        return String(firstDefined(value.serialNumber, value.customId, getPlainText(value)) ?? '');
    }

    if (source === DEVICE_CONFIG_SOURCE.CONTRACTOR || source === DEVICE_CONFIG_SOURCE.OPERATOR || source === DEVICE_CONFIG_SOURCE.DEVICE_GROUP) {
        return getPlainText(value);
    }

    return typeof value === 'object' ? getPlainText(value) : String(value);
};

const extractSourceValue = (item, source) => {
    const nestedSource = item?.source && typeof item.source === 'object' ? item.source : null;
    const rawValue = firstDefined(
        item?.sourceValue,
        item?.source_value,
        item?.sourceLabel,
        item?.source_label,
        item?.originValue,
        item?.sourceName,
        item?.sourceTarget,
        item?.subsetValue,
        item?.target,
        nestedSource?.value,
        nestedSource?.label,
        nestedSource?.name,
        nestedSource?.target,
    );

    return formatSourceValueByType(source, rawValue);
};

export const formatDeviceConfigSourceLabel = (source, sourceValue, translate) => {
    if (!source) {
        return sourceValue || '';
    }

    const typeLabel = translate(`resources.device.configSources.${source}`, { _: source });
    if (!sourceValue) {
        return typeLabel;
    }

    return `${typeLabel} - ${sourceValue}`;
};

const unwrapDeviceConfigPayload = payload => {
    if (payload == null) {
        return payload;
    }

    if (Array.isArray(payload)) {
        return payload;
    }

    const nested = payload.data;
    const hasOwnList = payload.parameters != null || payload.items != null || payload.entries != null || Array.isArray(payload.config);

    if (!hasOwnList && nested != null && (Array.isArray(nested) || typeof nested === 'object')) {
        return nested;
    }

    return payload;
};

const getDeviceConfigList = root => {
    if (Array.isArray(root)) {
        return root;
    }

    if (Array.isArray(root?.parameters)) {
        return root.parameters;
    }

    if (Array.isArray(root?.items)) {
        return root.items;
    }

    if (Array.isArray(root?.entries)) {
        return root.entries;
    }

    if (Array.isArray(root?.config)) {
        return root.config;
    }

    return [];
};

const getParameterKey = item => item?.parameter ?? item?.systemName ?? item?.system_name ?? item?.key ?? item?.code ?? item?.name ?? item?.label ?? '';

export const normalizeDeviceConfigParameter = (item, index) => {
    const source = normalizeDeviceConfigSource(item?.source ?? item?.origin ?? item?.sourceType);

    return {
        id: item?.id ?? `device-config-parameter-${index}`,
        name: item?.name ?? item?.label ?? getParameterKey(item),
        parameter: getParameterKey(item),
        value: stringifyDeviceConfigValue(item?.value ?? item?.resolvedValue),
        source,
        sourceValue: extractSourceValue(item, source),
        isSignificant: Boolean(item?.isSignificant ?? item?.is_significant ?? item?.significantChange ?? item?.significant),
    };
};

const mapFromConfigObject = config =>
    Object.entries(config).map(([parameter, value], index) =>
        normalizeDeviceConfigParameter(
            {
                parameter,
                value,
                name: parameter,
            },
            index,
        ),
    );

const getRawConfigString = root => {
    if (typeof root === 'string') {
        return root;
    }

    const candidates = [root?.fullConfigRaw, root?.fullConfig, root?.rawConfig, root?.config];
    return candidates.find(value => typeof value === 'string' && value !== '') ?? null;
};

const pickHighestPriorityParameters = parameters => {
    const byKey = new Map();

    asArray(parameters).forEach(item => {
        const key = item?.parameter || String(item?.id ?? '');
        if (!key) {
            return;
        }

        const current = byKey.get(key);
        const nextPriority = SOURCE_PRIORITY[item.source] ?? -1;
        const currentPriority = SOURCE_PRIORITY[current?.source] ?? -1;

        if (!current || nextPriority >= currentPriority) {
            byKey.set(key, item);
        }
    });

    return [...byKey.values()];
};

const getSummaryRoot = root => {
    if (!root || typeof root !== 'object' || Array.isArray(root)) {
        return {};
    }

    return root.device ?? root.summary ?? root.deviceSummary ?? root;
};

export const getDeviceConfigSummary = (device, configRecord) => {
    const fromConfig = getSummaryRoot(configRecord);
    const fromDevice = device && typeof device === 'object' ? device : {};
    const collectionPoint = firstDefined(fromConfig.collectionPoint, fromDevice.collectionPoint) ?? {};
    const contractor = firstDefined(fromConfig.contractor, fromDevice.contractor, fromDevice.group?.contractor);
    const model = firstDefined(fromConfig.model, fromConfig.deviceModel, fromDevice.model, fromDevice.deviceModel, fromDevice.modelCode);

    return {
        model: formatSourceValueByType(DEVICE_CONFIG_SOURCE.MODEL, model),
        contractor: String(
            firstDefined(typeof contractor === 'object' ? contractor?.name : contractor, fromConfig.contractorName, fromDevice.contractorName) ?? '',
        ),
        operator: String(
            firstDefined(
                fromConfig.operator,
                typeof collectionPoint === 'object' ? collectionPoint.operator : null,
                fromDevice.operator,
                fromDevice.collectionPoint?.operator,
            ) ?? '',
        ),
        collectionPoint: formatCollectionPointSourceValue(
            firstDefined(fromConfig.collectionPoint, fromDevice.collectionPoint, fromConfig.collectionPointLabel, fromDevice.collectionPointLabel),
        ),
    };
};

export const normalizeDeviceConfigRecord = (payload, fallbackId) => {
    const root = unwrapDeviceConfigPayload(payload);
    const rawConfig = getRawConfigString(root);
    const list = getDeviceConfigList(root);
    const hasConfigObject =
        root && typeof root === 'object' && !Array.isArray(root) && root.config && typeof root.config === 'object' && !Array.isArray(root.config);
    const parameters =
        list.length === 0 && hasConfigObject
            ? mapFromConfigObject(root.config)
            : asArray(list).map((item, index) => normalizeDeviceConfigParameter(item, index));

    return {
        id: (Array.isArray(root) ? fallbackId : root?.id) ?? fallbackId,
        parameters: pickHighestPriorityParameters(parameters),
        rawConfig,
        summary: getDeviceConfigSummary(null, root),
    };
};

export const formatDeviceConfigForClipboard = (parameters, rawConfig) => {
    if (typeof rawConfig === 'string' && rawConfig.trim() !== '') {
        return rawConfig;
    }

    return asArray(parameters)
        .map(item => {
            const key = item?.parameter || item?.name;
            if (!key) {
                return null;
            }

            return `${key} = ${item.value ?? ''}`;
        })
        .filter(Boolean)
        .join('\n');
};

export const copyTextToClipboard = async text => {
    if (!navigator.clipboard?.writeText) {
        return false;
    }

    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        return false;
    }
};
